/**
 * Multiplayer client.
 *
 * The socket carries inputs out and snapshots in. The local player's car is
 * *predicted* with the same shared physics and then reconciled against the
 * server's authoritative position, while remote cars are interpolated between
 * the last two snapshots so they stay smooth on a jittery connection.
 */

import {
  NET, decodeMessage, encodeMessage,
  type ClientMessage, type LobbyInfo, type LobbyMember, type LobbySettings,
  type RaceModeId, type RaceResultRow, type ServerMessage, type SnapshotCar,
} from '@shared/net.js';

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'error' | 'closed';

export interface RemoteCarState {
  id: string;
  prev: SnapshotCar;
  next: SnapshotCar;
  /** Interpolation alpha between prev and next in 0..1. */
  alpha: number;
  /** Rendered values, refreshed by `interpolate()`. */
  x: number;
  y: number;
  angle: number;
  speed: number;
  slip: number;
  boost: boolean;
}

export interface LobbySnapshot {
  code: string;
  name: string;
  settings: LobbySettings;
  members: LobbyMember[];
  hostId: string;
  inProgress: boolean;
}

export class NetClient {
  state: ConnectionState = 'idle';
  playerId = '';
  lobbies: LobbyInfo[] = [];
  lobby: LobbySnapshot | null = null;
  error = '';
  ping = 0;

  /** Remote cars keyed by racer id. */
  readonly remote = new Map<string, RemoteCarState>();
  lastSnapshotCars: SnapshotCar[] = [];
  /** Server race clock at the last snapshot. */
  raceTime = 0;
  results: RaceResultRow[] | null = null;
  /**
   * Completed laps reported by the server, newest last. Snapshots carry only the
   * lap *count*, so the lap time and personal-best flags have to arrive as events.
   */
  readonly laps: Array<{ playerId: string; lap: number; lapTime: number; bestLap: number; valid: boolean; position: number; at: number }> = [];
  chat: Array<{ from: string; text: string; at: number; system?: boolean }> = [];

  private socket: WebSocket | null = null;
  /** Messages written before the socket finished opening. */
  private pending: ClientMessage[] = [];
  private seq = 0;
  private pingTimer: number | null = null;
  private lastPingSent = 0;
  private reconnectAttempts = 0;
  private handlers = new Set<(msg: ServerMessage) => void>();
  private snapshotInterval = 1 / NET.snapshotRate;

  onMessage(fn: (msg: ServerMessage) => void): () => void {
    this.handlers.add(fn);
    return () => this.handlers.delete(fn);
  }

  connect(): void {
    if (this.socket && (this.state === 'connected' || this.state === 'connecting')) return;
    this.state = 'connecting';
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    let socket: WebSocket;
    try {
      socket = new WebSocket(`${protocol}//${location.host}/ws`);
    } catch {
      this.state = 'error';
      this.error = 'Could not open a connection to the race server.';
      return;
    }
    this.socket = socket;
    // Every callback checks that it still belongs to the live socket. A socket
    // that was replaced (or deliberately closed) must not be allowed to reset
    // the state of its successor — that race silently dropped a join request
    // queued behind it and left the lobby screen hanging.
    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.state = 'connected';
      this.error = '';
      this.reconnectAttempts = 0;
      this.send({ t: 'hello' });
      this.send({ t: 'listLobbies' });
      this.startPing();
      this.flushPending();
    };
    socket.onmessage = (event) => {
      if (this.socket !== socket) return;
      const msg = decodeMessage<ServerMessage>(typeof event.data === 'string' ? event.data : '');
      if (msg) this.handle(msg);
    };
    socket.onerror = () => {
      if (this.socket !== socket) return;
      this.state = 'error';
      this.error = 'Connection error.';
    };
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.state = 'closed';
      this.stopPing();
      this.pending = [];
      this.socket = null;
      this.lobby = null;
      this.remote.clear();
    };
  }

  disconnect(): void {
    this.stopPing();
    if (this.socket) {
      try {
        this.socket.close(1000, 'leaving');
      } catch {
        /* already gone */
      }
    }
    this.socket = null;
    this.pending = [];
    this.state = 'idle';
    this.lobby = null;
    this.remote.clear();
    this.results = null;
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.lastPingSent = Date.now();
      this.send({ t: 'ping', time: this.lastPingSent });
    }, 3000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  send(message: ClientMessage): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      // Join/create requests routinely arrive while the socket is still opening
      // (the player clicked immediately). Dropping them silently left the lobby
      // screens hanging on "joining…", so hold them until the handshake lands.
      if (this.state === 'connecting' && this.pending.length < 32) this.pending.push(message);
      return;
    }
    try {
      this.socket.send(encodeMessage(message));
    } catch {
      /* the close handler will clean up */
    }
  }

  private flushPending(): void {
    if (this.pending.length === 0) return;
    const queued = this.pending;
    this.pending = [];
    for (const message of queued) this.send(message);
  }

  private handle(msg: ServerMessage): void {
    switch (msg.t) {
      case 'welcome':
        this.playerId = msg.playerId;
        break;
      case 'lobbies':
        this.lobbies = msg.lobbies;
        break;
      case 'lobby':
        this.lobby = {
          code: msg.code, name: msg.name, settings: msg.settings,
          members: msg.members, hostId: msg.hostId, inProgress: msg.inProgress,
        };
        this.results = null;
        break;
      case 'joined':
        break;
      case 'start':
        // A new race begins. `laps` is consumed by the fresh RaceSession with its
        // own cursor, so leaving the previous race's laps in place would replay
        // all of them as banners and compute the best lap from stale data.
        this.laps.length = 0;
        this.results = null;
        break;
      case 'left':
        this.lobby = null;
        this.remote.clear();
        this.laps.length = 0;
        this.results = null;
        break;
      case 'snapshot':
        this.applySnapshot(msg.cars, msg.time, msg.serverTime);
        break;
      case 'results':
        this.results = msg.rows;
        break;
      case 'lap':
        this.laps.push({ ...msg, at: Date.now() });
        if (this.laps.length > 120) this.laps.shift();
        break;
      case 'pong':
        if (this.lastPingSent > 0) this.ping = Date.now() - this.lastPingSent;
        break;
      case 'chat':
        this.chat.push({ from: msg.from, text: msg.text, at: Date.now(), system: msg.system });
        if (this.chat.length > 60) this.chat.shift();
        break;
      case 'error':
        if (msg.code !== 'payout') this.error = msg.message;
        break;
      default:
        break;
    }
    for (const fn of this.handlers) fn(msg);
  }

  /**
   * Stores the newest snapshot and re-bases interpolation. The previous "next"
   * becomes the new "prev", which keeps a continuous timeline even if a packet
   * is lost.
   */
  private applySnapshot(cars: SnapshotCar[], raceTime: number, serverTime: number): void {
    this.raceTime = raceTime;
    this.lastSnapshotCars = cars;
    void serverTime;
    const seen = new Set<string>();
    for (const car of cars) {
      if (car.id === this.playerId) continue;
      seen.add(car.id);
      const existing = this.remote.get(car.id);
      if (existing) {
        existing.prev = existing.alpha >= 0.9 ? car : existing.next;
        existing.next = car;
        existing.alpha = 0;
      } else {
        this.remote.set(car.id, {
          id: car.id,
          prev: { ...car },
          next: car,
          alpha: 1,
          x: car.x, y: car.y, angle: car.angle, speed: car.speed, slip: car.slip, boost: false,
        });
      }
    }
    for (const id of [...this.remote.keys()]) if (!seen.has(id)) this.remote.delete(id);
  }

  /** Advances interpolation. Call once per frame with the frame delta. */
  interpolate(dt: number): void {
    const step = dt / this.snapshotInterval;
    for (const car of this.remote.values()) {
      car.alpha = Math.min(1, car.alpha + step);
      const t = car.alpha;
      const a = car.prev;
      const b = car.next;
      car.x = a.x + (b.x - a.x) * t;
      car.y = a.y + (b.y - a.y) * t;
      car.speed = a.speed + (b.speed - a.speed) * t;
      car.slip = b.slip;
      car.boost = b.speed > a.speed + 15;
      // Shortest-path angle interpolation so cars do not spin the wrong way.
      let delta = b.angle - a.angle;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      car.angle = a.angle + delta * t;
    }
  }

  /** Finds the server's authoritative snapshot for the local player. */
  localSnapshot(): SnapshotCar | null {
    return this.lastSnapshotCars.find((c) => c.id === this.playerId) ?? null;
  }

  sendInput(input: { throttle: number; brake: number; steer: number; handbrake: boolean; boost: boolean }): void {
    this.send({ t: 'input', seq: this.seq++, ...input });
  }

  /** Asks the server to put our car back on the racing line. */
  requestReset(): void {
    this.send({ t: 'reset' });
  }

  createLobby(name: string, settings: LobbySettings): void {
    this.send({ t: 'createLobby', name, settings });
  }

  joinLobby(code: string): void {
    this.send({ t: 'joinLobby', code: code.toUpperCase() });
  }

  leaveLobby(): void {
    this.send({ t: 'leaveLobby' });
    this.lobby = null;
    this.results = null;
  }

  setReady(ready: boolean): void {
    this.send({ t: 'ready', ready });
  }

  select(carId: string, color: string): void {
    this.send({ t: 'select', carId, color });
  }

  updateSettings(settings: Partial<LobbySettings>): void {
    this.send({ t: 'settings', settings });
  }

  startRace(): void {
    this.send({ t: 'start' });
  }

  sendChat(text: string): void {
    this.send({ t: 'chat', text });
  }

  isHost(): boolean {
    return this.lobby !== null && this.lobby.hostId === this.playerId;
  }

  get connected(): boolean {
    return this.state === 'connected';
  }
}

export type { SnapshotCar, RaceResultRow, LobbyInfo, LobbyMember, LobbySettings, RaceModeId };
